// Interest Modeling carry-portfolio preview: the wire contract of the ephemeral
// reprice endpoint plus the PURE derivations that turn its per-tract + recombined
// payloads into the KPI / chart / summary-table shapes.
//
// The math is SERVER-side (the calculator reprices each edited tract; the BE
// recombines the cross-tract aggregate). This module only CUMULATES the returned
// per-month series (running sum) and reshapes them — everything here is pure and
// unit-tested; the live wiring (token, cache, warm/reprice/drop, Pusher) lives in
// `use-carry-preview.ts`, and the SciChart rendering in `carry-chart.tsx`.
import { withSubtractCoverage } from 'utils/subtract-coverage';
import {
  CARRY_TRIGGER_BY_ID,
  deriveCarryDirection,
} from './interest-modeling-carry';
import type { CarryDirection, CarryGroup, InterestItem } from './types';

// ── Wire types (interest_preview response, camelCase) ─────────────────────────

/**
 * One monthly point on a carry series — return + expense for the BASE leg (no
 * modeling) and the WORKING leg (the modeled interest). The surface renders the
 * working leg; the base leg rides along for a future "vs without modeling" overlay.
 */
export interface CarrySeriesPoint {
  date: string;
  baseReturn: number;
  baseExpense: number;
  workingReturn: number;
  workingExpense: number;
}

/** One carry group's reprice rollup within a tract (the calculator's group grain). */
export interface PreviewGroup {
  id: string;
  name: string;
  tractId: string;
  wellIds: string[];
  /** Payout DATES — distinct, sorted ISO dates the group's wells pay out on. A group spans
   * wells with distinct first-production / capex, so it can flip more than once. The FE
   * converts these to month indices against the PORTFOLIO x-axis (buildChartModel /
   * buildSummaryRows) — a view concern, so a marker lands on the exact month the chart draws,
   * even across tracts that start on different dates. Empty ⇒ the group never pays out. */
  payoutDates: string[];
  /** Per-well payouts, date-sorted — which well pays out on which date, so chart markers can
   * name the well(s). Wells that never pay out are excluded. Optional: an older cached payload
   * lacks it, and markers then fall back to the plain per-date group label. */
  payoutWells?: { wellId: string; date: string }[];
  netToUsBase: number;
  netToUsWorking: number;
  series: CarrySeriesPoint[];
}

/** One well (slot) series — shipped ONLY when the request's `view === 'byProperty'`. */
export interface PreviewSlot {
  uniqueSlotId: string;
  groupId: string;
  series: CarrySeriesPoint[];
}

/** The per-tract reprice payload (the calculator's response, slots gated by view). */
export interface PreviewTract {
  calculationId?: number;
  groups: PreviewGroup[];
  slots?: PreviewSlot[];
  aggregate: CarrySeriesPoint[];
  elections?: unknown[]; // Elections — ignored on the carry surface.
}

/** Success response of `POST interest_preview` — the tract's payload + the recombined portfolio. */
export interface InterestPreviewSuccess {
  token: string;
  tract: PreviewTract;
  aggregate: CarrySeriesPoint[];
}

/**
 * Case-1 async-prepare hook (`needs_prepare`/`preparing`), the old-calc
 * `needs_recalculation` prompt (a committed calc that predates the pairs sidecar), or a
 * soft `error` — none block editing.
 */
export interface InterestPreviewPending {
  token: string;
  status: 'needs_prepare' | 'preparing' | 'needs_recalculation' | 'error';
  error?: string;
}

export type InterestPreviewResponse =
  InterestPreviewSuccess | InterestPreviewPending;

/** Response of `POST interest_preview_drop` — the recombined portfolio of the remaining tracts. */
export interface InterestPreviewDropResponse {
  token: string;
  aggregate: CarrySeriesPoint[];
}

/**
 * Narrows a reprice response to the success case (no `status` field).
 *
 * @param {InterestPreviewResponse} response - The reprice response.
 * @return {boolean} True when the response carries a tract payload.
 */
export function isPreviewSuccess(
  response: InterestPreviewResponse
): response is InterestPreviewSuccess {
  return !('status' in response);
}

// ── Client cache + view types ─────────────────────────────────────────────────

/** A tract's last-good reprice payload, cached client-side (groups always; slots once byProperty fetched). */
export interface TractPreviewResult {
  groups: PreviewGroup[];
  slots?: PreviewSlot[];
  aggregate: CarrySeriesPoint[];
}

/** A cached tract paired with its display name + total-well count (for labels + "n/total"). */
export interface CachedTract {
  tractId: string;
  name: string;
  /** A Tract the package holds in part: its "(N of M Subtracts)" (see `chartTractName`). */
  coverage?: string | null;
  totalWells: number;
  result: TractPreviewResult;
}

/** The three chart modes (Carbon RadioButtonGroup): portfolio / per-group / per-well. */
export type ChartView = 'aggregate' | 'byAsset' | 'byProperty';

/**
 * Distinct palette shared by the chart series and the summary-table color dots so a
 * group reads the same color in both (mock `ASSET_COLORS`). Index-aligned with the
 * flattened group order that both derivations iterate.
 */
export const ASSET_COLORS = [
  '#0f62fe',
  '#8a3ffc',
  '#007d79',
  '#ee5396',
  '#b28600',
] as const;

/**
 * The palette color for a flattened-group/well index (wraps past the palette length).
 *
 * @param {number} index - The flattened group/well index.
 * @return {string} The hex color.
 */
export function colorForIndex(index: number): string {
  return ASSET_COLORS[index % ASSET_COLORS.length];
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

type CarryLeg = 'working' | 'base';

/**
 * Running-sum a series' return leg into a cumulative array (per-month totals).
 *
 * @param {CarrySeriesPoint[]} series - The monthly series.
 * @param {CarryLeg} [leg] - Which leg to sum (default working).
 * @return {number[]} The cumulative return by month.
 */
export function cumulativeReturn(
  series: CarrySeriesPoint[],
  leg: CarryLeg = 'working'
): number[] {
  const key = leg === 'working' ? 'workingReturn' : 'baseReturn';
  return runningSum(series.map((point) => point[key]));
}

/**
 * Running-sum a series' expense leg into a cumulative array (per-month totals).
 *
 * @param {CarrySeriesPoint[]} series - The monthly series.
 * @param {CarryLeg} [leg] - Which leg to sum (default working).
 * @return {number[]} The cumulative expense by month.
 */
export function cumulativeExpense(
  series: CarrySeriesPoint[],
  leg: CarryLeg = 'working'
): number[] {
  const key = leg === 'working' ? 'workingExpense' : 'baseExpense';
  return runningSum(series.map((point) => point[key]));
}

export function runningSum(values: number[]): number[] {
  const out: number[] = [];
  let total = 0;
  for (const value of values) {
    total += Number.isFinite(value) ? value : 0;
    out.push(total);
  }
  return out;
}

/**
 * The tract display name minus a trailing "(OGL)"/"(UMI)" suffix, if any (mock parity).
 *
 * @param {string} name - The tract display name.
 * @return {string} The trimmed name.
 */
export function shortTractName(name: string): string {
  return name.replace(/\s*\(.*\)\s*$/, '') || name;
}

/**
 * The tract as the charts and the summary name it: its short name, then the "(N of M
 * Subtracts)" of a Tract the package holds in part. The label goes on after shortening, never
 * into the stored name — `shortTractName` drops a trailing "(...)".
 *
 * @param {string} name - The tract display name.
 * @param {string | null | undefined} coverage - Its `subtractCoverageLabel`, or null.
 * @return {string} The short name, then the label when there is one.
 */
export function chartTractName(name: string, coverage?: string | null): string {
  return withSubtractCoverage(shortTractName(name), coverage);
}

// ── KPIs ──────────────────────────────────────────────────────────

export interface PortfolioKpis {
  /**
   * First 1-based month cumulative working return ≥ cumulative working expense,
   * counted only ONCE expenses have started (cumulative expense > 0) — a payout
   * can't precede the first dollar spent.
   */
  breakevenMonth: number | null;
  /** Final cumulative working return − working expense ($). */
  netToUs: number;
  /** Deepest (cumReturn − cumExpense) drawdown ($, ≤ 0). */
  peakExposure: number;
}

/**
 * Portfolio KPIs from the recombined aggregate (working leg): breakeven month,
 * final net-to-us, and peak exposure (deepest drawdown).
 *
 * @param {CarrySeriesPoint[]} aggregate - The recombined cross-tract series.
 * @return {PortfolioKpis} The three KPI values.
 */
export function portfolioKpis(aggregate: CarrySeriesPoint[]): PortfolioKpis {
  const cumReturn = cumulativeReturn(aggregate);
  const cumExpense = cumulativeExpense(aggregate);
  let breakevenMonth: number | null = null;
  let peakExposure = 0;
  for (let i = 0; i < aggregate.length; i++) {
    const net = cumReturn[i] - cumExpense[i];
    if (net < peakExposure) {
      peakExposure = net;
    }
    // Breakeven is the first payout AFTER spending starts — before the first dollar
    // of expense, net is trivially ≥ 0 (0 − 0), which is not a real breakeven.
    if (breakevenMonth === null && cumExpense[i] > 0 && net >= 0) {
      breakevenMonth = i + 1;
    }
  }
  const last = aggregate.length - 1;
  const netToUs = last >= 0 ? cumReturn[last] - cumExpense[last] : 0;
  return { breakevenMonth, netToUs, peakExposure };
}

// ── Summary table ──────────────────────────────────────────────────────

/** Summary-table direction — the derived carry direction, or a full working interest. */
export type SummaryDirection = CarryDirection | 'working';

/** One summary-table row: one carry group on one tract. */
export interface SummaryRow {
  /** Tract-scoped (`tractId:groupId`) — the full-WI id repeats on every tract. */
  id: string;
  tractId: string;
  /**
   * The group's position in the chart model's flattened group order (its band color);
   * null until the server has priced the group, since an unpriced group has no band.
   */
  colorIndex: number | null;
  label: string; // "Tract · Group"
  direction: SummaryDirection;
  wellCount: number;
  totalWells: number;
  payoutMonth: number | null;
  netToUs: number;
  multiple: number;
}

/**
 * A carry tract as the CLIENT knows it — its live groups + roster size — with its cached
 * server result once priced (absent until the first reprice lands).
 */
export interface SummaryTract {
  tractId: string;
  name: string;
  /** A Tract the package holds in part: its "(N of M Subtracts)" (see `chartTractName`). */
  coverage?: string | null;
  totalWells: number;
  groups: CarryGroup[];
  result?: TractPreviewResult;
}

/**
 * The reserved id the eval calculator stamps on the implicit full-participation entry it
 * synthesizes for a tract's unassigned wells (wells on the roster but in no carry group).
 * Matched by id, not display name, so a user group happening to be named "Working
 * interest" is never mistaken for the full-participation row.
 */
export const FULL_WI_GROUP_ID = 'full-wi';

/**
 * Summary rows — one per CLIENT carry group, in item order then group order, so a group
 * has a row the moment it exists (placeholders: no payout, $0, 0.00×) instead of only once
 * the server has priced it. When the tract's cached result carries the group (by id) the
 * row shows the live values: payout month on the portfolio axis, net-to-us, multiple. A
 * group the server omits (all of its wells priced at zero) keeps its placeholder row; a
 * server group with no client group (deleted, still in the cache) gets none. The implicit
 * full-working-interest entry the calculator synthesizes for a tract's unassigned wells
 * exists only server-side, so it is appended after that tract's client rows once cached.
 *
 * Color alignment: `colorIndex` is the group's position in the chart model's flattened
 * group order — priced tracts in the given order × their server groups, the same walk
 * `buildChartModel` does — so a priced row's dot matches its chart band. Direction comes
 * from the CLIENT group's BPO/APO WI (the reprice payload doesn't carry the split).
 *
 * @param {SummaryTract[]} tracts - The carry tracts in interest-item order, each with its
 *   live groups and (once priced) its cached result.
 * @param {CarrySeriesPoint[]} portfolioAggregate - The recombined portfolio; the axis the
 *   "Payout" cell's month index is measured on (same axis the chart uses).
 * @return {SummaryRow[]} The ordered summary rows.
 */
export function buildSummaryRows(
  tracts: SummaryTract[],
  portfolioAggregate: CarrySeriesPoint[]
): SummaryRow[] {
  const monthByDate = monthIndexByDate(
    portfolioAggregate.map((point) => point.date)
  );
  const colorIndexByRowId = new Map<string, number>();
  for (const { tractId, result } of tracts) {
    for (const group of result?.groups ?? []) {
      colorIndexByRowId.set(rowId(tractId, group.id), colorIndexByRowId.size);
    }
  }
  const rows: SummaryRow[] = [];
  for (const {
    tractId,
    name,
    coverage,
    totalWells,
    groups,
    result,
  } of tracts) {
    const short = chartTractName(name, coverage);
    const serverGroups = new Map(
      (result?.groups ?? []).map((group) => [group.id, group])
    );
    for (const group of groups) {
      const id = rowId(tractId, group.id);
      rows.push({
        id,
        tractId,
        colorIndex: colorIndexByRowId.get(id) ?? null,
        label: `${short} · ${group.name}`,
        // These rows are built from groups that already passed isCarryGroupValid,
        // so the APO half is set; the fallback is unreachable and only states the
        // default a group without one would carry.
        direction: deriveCarryDirection(group.bpoWi, group.apoWi) ?? 'carrying',
        wellCount: group.wellIds.length,
        totalWells,
        ...pricedCells(serverGroups.get(group.id), monthByDate),
      });
    }
    const fullWi = serverGroups.get(FULL_WI_GROUP_ID);
    if (fullWi) {
      const id = rowId(tractId, fullWi.id);
      rows.push({
        id,
        tractId,
        colorIndex: colorIndexByRowId.get(id) ?? null,
        label: `${short} · ${fullWi.name}`,
        direction: 'working',
        wellCount: fullWi.wellIds.length,
        totalWells,
        ...pricedCells(fullWi, monthByDate),
      });
    }
  }
  return rows;
}

// Tract-scoped row identity — the full-WI id is the same on every tract.
function rowId(tractId: string, groupId: string): string {
  return `${tractId}:${groupId}`;
}

// The server-priced cells of a row; placeholders until the server has priced the group.
function pricedCells(
  group: PreviewGroup | undefined,
  monthByDate: Map<string, number>
): Pick<SummaryRow, 'payoutMonth' | 'netToUs' | 'multiple'> {
  if (!group) {
    return { payoutMonth: null, netToUs: 0, multiple: 0 };
  }
  const finalReturn = lastOf(cumulativeReturn(group.series));
  const finalExpense = lastOf(cumulativeExpense(group.series));
  return {
    payoutMonth: payoutMonthsOnAxis(group.payoutDates, monthByDate)[0] ?? null,
    netToUs: Number.isFinite(group.netToUsWorking)
      ? group.netToUsWorking
      : finalReturn - finalExpense,
    multiple: finalExpense > 0 ? finalReturn / finalExpense : 0,
  };
}

function lastOf(values: number[]): number {
  return values.length ? values[values.length - 1] : 0;
}

/**
 * The portfolio aggregate with the excluded rows' contributions removed, by date. A row id
 * that isn't a priced group (unpriced, deleted, or not found) contributes nothing. When
 * nothing is excluded the input series is returned unchanged, so the default view is
 * identical to today. Pure and view-time only — the include/exclude toggle is a what-if; it
 * never reprices and never changes what Calculate commits. Because the redesign makes a
 * group's legs sum exactly into the recombined portfolio, subtracting a group's series is
 * exact (its net-to-us drops out of the total, as the summary row's own value shows).
 *
 * @param {CarrySeriesPoint[]} portfolioAggregate - The recombined portfolio series.
 * @param {ReadonlySet<string>} excludedRowIds - Summary row ids (`tractId:groupId`) to remove.
 * @param {SummaryTract[]} tracts - The carry tracts; their cached groups carry the series.
 * @return {CarrySeriesPoint[]} The aggregate less the excluded rows (same dates + length).
 */
export function portfolioAggregateExcluding(
  portfolioAggregate: CarrySeriesPoint[],
  excludedRowIds: ReadonlySet<string>,
  tracts: SummaryTract[]
): CarrySeriesPoint[] {
  if (excludedRowIds.size === 0) {
    return portfolioAggregate;
  }
  const excludedByDate = new Map<string, CarrySeriesPoint>();
  for (const { tractId, result } of tracts) {
    for (const group of result?.groups ?? []) {
      if (!excludedRowIds.has(rowId(tractId, group.id))) {
        continue;
      }
      for (const point of group.series) {
        const acc = excludedByDate.get(point.date);
        if (acc) {
          acc.baseReturn += point.baseReturn;
          acc.baseExpense += point.baseExpense;
          acc.workingReturn += point.workingReturn;
          acc.workingExpense += point.workingExpense;
        } else {
          excludedByDate.set(point.date, { ...point });
        }
      }
    }
  }
  if (excludedByDate.size === 0) {
    return portfolioAggregate;
  }
  return portfolioAggregate.map((point) => {
    const excl = excludedByDate.get(point.date);
    if (!excl) {
      return point;
    }
    return {
      date: point.date,
      baseReturn: point.baseReturn - excl.baseReturn,
      baseExpense: point.baseExpense - excl.baseExpense,
      workingReturn: point.workingReturn - excl.workingReturn,
      workingExpense: point.workingExpense - excl.workingExpense,
    };
  });
}

// ── Chart model ────────────────────────────────────────────────────────

/** One payout marker position for a group: the axis month + the wells paying out then. */
export interface PayoutMark {
  month: number;
  /** Display names of the wells paying out this month (roster name, falling back to the raw
   * well id). Empty when the payload predates per-well payouts — the marker then keeps the
   * plain group label. */
  wellNames: string[];
}

/** One stacked band in the byAsset view (a group) — carries its payout marks. */
export interface ChartGroupRow {
  id: string;
  tractId: string;
  label: string; // "Tract · Group"
  cumReturn: number[];
  cumExpense: number[];
  payoutMarks: PayoutMark[];
}

/** One stacked band in the byProperty view (a well). */
export interface ChartWellRow {
  id: string;
  tractId: string;
  label: string; // "Tract · Well"
  cumReturn: number[];
  cumExpense: number[];
}

/** The chart's whole data model — portfolio curves + per-group + per-well bands. */
export interface CarryChartModel {
  months: number[];
  aggReturn: number[];
  aggExpense: number[];
  breakevenMonth: number | null;
  groups: ChartGroupRow[];
  wells: ChartWellRow[];
}

/**
 * Assembles the chart model from the recombined portfolio aggregate + the cached
 * per-tract payloads. Cumulates every series (working leg). `groups`/`wells` span
 * ALL cached tracts (the chart scopes to the focused tract at draw time); `wellNames`
 * resolves a slot id to its display name for the byProperty labels.
 *
 * @param {CarrySeriesPoint[]} portfolioAggregate - The recombined cross-tract series.
 * @param {CachedTract[]} cachedTracts - The cached per-tract payloads (stable order).
 * @param {Map<string, string>} wellNames - Slot id → well name (byProperty labels).
 * @param {Map<string, CarryGroup>} clientGroupsById - Client carry groups by id; supplies the
 *   live group name for legends/markers (a rename doesn't reprice, so the server payload keeps
 *   the old name). Falls back to the server group name.
 * @return {CarryChartModel} The chart model.
 */
/**
 * Maps each portfolio-axis date to its 1-based month index (the chart x-axis position).
 *
 * @param {string[]} axisDates - The portfolio series' dates, in order.
 * @return {Map<string, number>} Date → 1-based month index.
 */
function monthIndexByDate(axisDates: string[]): Map<string, number> {
  const map = new Map<string, number>();
  axisDates.forEach((date, i) => map.set(date, i + 1));
  return map;
}

/**
 * Converts payout DATES to month indices on the portfolio axis, dropping any date not on the
 * axis. Input is already distinct + sorted (backend), so the output stays ascending. This is
 * where a group's payout timing is placed relative to the SHARED chart axis — the same axis
 * `alignCumulativeToAxis` maps series onto — so markers land correctly across tracts.
 *
 * @param {string[]} payoutDates - The group's distinct, sorted ISO payout dates.
 * @param {Map<string, number>} monthByDate - Portfolio date → 1-based month index.
 * @return {number[]} The payout month indices on the portfolio axis.
 */
function payoutMonthsOnAxis(
  payoutDates: string[],
  monthByDate: Map<string, number>
): number[] {
  return (payoutDates ?? [])
    .map((date) => monthByDate.get(date))
    .filter((month): month is number => month !== undefined);
}

/**
 * Builds a group's payout marks on the portfolio axis: one mark per payout month, carrying the
 * names of the wells paying out that month (same-month payouts merge into one mark instead of
 * stacking duplicate markers). Names resolve from the wellsByTract roster (`wellNames`),
 * falling back to the raw well id. A payload without `payoutWells` (older cached response)
 * falls back to the per-date marks with no well names — the plain group label.
 *
 * @param {PreviewGroup} group - The server group (payoutWells / payoutDates).
 * @param {Map<string, number>} monthByDate - Portfolio date → 1-based month index.
 * @param {Map<string, string>} wellNames - Well id → display name.
 * @return {PayoutMark[]} The group's payout marks, ascending by month.
 */
function payoutMarksOnAxis(
  group: PreviewGroup,
  monthByDate: Map<string, number>,
  wellNames: Map<string, string>
): PayoutMark[] {
  const entries = group.payoutWells ?? [];
  if (entries.length === 0) {
    return payoutMonthsOnAxis(group.payoutDates, monthByDate).map((month) => ({
      month,
      wellNames: [],
    }));
  }
  const namesByMonth = new Map<number, string[]>();
  for (const { wellId, date } of entries) {
    const month = monthByDate.get(date);
    if (month === undefined) {
      continue; // date not on the portfolio axis — same rule as payoutMonthsOnAxis
    }
    const names = namesByMonth.get(month) ?? [];
    names.push(wellNames.get(wellId) ?? wellId);
    namesByMonth.set(month, names);
  }
  return [...namesByMonth.entries()]
    .sort(([a], [b]) => a - b)
    .map(([month, names]) => ({ month, wellNames: names }));
}

export function buildChartModel(
  portfolioAggregate: CarrySeriesPoint[],
  cachedTracts: CachedTract[],
  wellNames: Map<string, string>,
  clientGroupsById: Map<string, CarryGroup> = new Map(),
  excludedRowIds: ReadonlySet<string> = new Set()
): CarryChartModel {
  const aggReturn = cumulativeReturn(portfolioAggregate);
  const aggExpense = cumulativeExpense(portfolioAggregate);
  // The shared x-axis is the portfolio's month grid (the union of every tract's span). Every
  // group / well series is aligned onto it BY DATE (see alignCumulativeToAxis) so all series —
  // and thus every chart's xValues/yValues — share this length.
  const axisDates = portfolioAggregate.map((point) => point.date);
  const months = axisDates.map((_, i) => i + 1);
  const monthByDate = monthIndexByDate(axisDates);
  const { breakevenMonth } = portfolioKpis(portfolioAggregate);

  const groups: ChartGroupRow[] = [];
  const wells: ChartWellRow[] = [];
  for (const { tractId, name, coverage, result } of cachedTracts) {
    const short = chartTractName(name, coverage);
    for (const group of result.groups) {
      if (excludedRowIds.has(rowId(tractId, group.id))) {
        continue; // an excluded summary row drops its band from byAsset too
      }
      groups.push({
        id: group.id,
        tractId,
        // Live client name when present (a rename doesn't reprice, so the server keeps
        // the old name); fall back to the server group name.
        label: `${short} · ${
          clientGroupsById.get(group.id)?.name ?? group.name
        }`,
        cumReturn: alignCumulativeToAxis(
          group.series,
          cumulativeReturn(group.series),
          axisDates
        ),
        cumExpense: alignCumulativeToAxis(
          group.series,
          cumulativeExpense(group.series),
          axisDates
        ),
        payoutMarks: payoutMarksOnAxis(group, monthByDate, wellNames),
      });
    }
    for (const slot of result.slots ?? []) {
      if (excludedRowIds.has(rowId(tractId, slot.groupId))) {
        continue; // its group is excluded — drop its band from byProperty too
      }
      wells.push({
        id: slot.uniqueSlotId,
        tractId,
        label: `${short} · ${
          wellNames.get(slot.uniqueSlotId) ?? slot.uniqueSlotId
        }`,
        cumReturn: alignCumulativeToAxis(
          slot.series,
          cumulativeReturn(slot.series),
          axisDates
        ),
        cumExpense: alignCumulativeToAxis(
          slot.series,
          cumulativeExpense(slot.series),
          axisDates
        ),
      });
    }
  }
  return { months, aggReturn, aggExpense, breakevenMonth, groups, wells };
}

/**
 * Aligns one entity's cumulative series onto the shared portfolio month axis, BY DATE. The
 * calculator returns each entity's series only over the months it has data — a well's
 * production life is shorter than the portfolio's union span, so its array is shorter than the
 * x-axis. (The mock never hit this: it built every series on one fixed HORIZON grid. Real
 * series have per-entity lengths, and SciChart requires xValues.length === yValues.length.)
 * Forward-fill the running cumulative onto the axis: 0 before the entity's first month, its
 * cumulative during, and its final value flat after it ends — matching how a cumulative line
 * reads (nothing yet, then accrues, then holds).
 *
 * @param {{ date: string }[]} series - The entity's own monthly series (carries the dates). Widened
 *   from `CarrySeriesPoint[]` so single-leg election series (`{date, return, expense}`) reuse it too.
 * @param {number[]} cumulative - Its cumulative values, index-aligned to `series`.
 * @param {string[]} axisDates - The shared axis dates (the portfolio series' dates), in order.
 * @return {number[]} Cumulative values at each axis month (length === axisDates.length).
 */
export function alignCumulativeToAxis(
  series: { date: string }[],
  cumulative: number[],
  axisDates: string[]
): number[] {
  const byDate = new Map<string, number>();
  series.forEach((point, i) => byDate.set(point.date, cumulative[i]));

  const out = new Array<number>(axisDates.length);
  let running = 0;
  for (let i = 0; i < axisDates.length; i++) {
    const value = byDate.get(axisDates[i]);
    if (value !== undefined) {
      running = value;
    }
    out[i] = running;
  }
  return out;
}

// ── Chart draw data (pure — the chart just paints this) ───────────────────────

/** One stacked series to draw (byAsset/byProperty). */
export interface ChartStackRow {
  id: string;
  label: string;
  values: number[];
  color: string;
}

/** A payout / breakeven vertical marker. */
export interface ChartMarker {
  month: number;
  label: string;
  color: string;
}

/** One legend entry mirroring a drawn series. */
export interface ChartLegendItem {
  label: string;
  color: string;
}

/** Everything the SciChart layer needs for one (view, focus) — return curve(s), expense line, markers, legend. */
export interface ChartRenderData {
  months: number[];
  /** Aggregate view: the single portfolio cumulative-return mountain. Null on scoped views. */
  aggregateReturn: number[] | null;
  /** Scoped views: one stacked band per group (byAsset) / well (byProperty). Null on aggregate. */
  stackRows: ChartStackRow[] | null;
  /** The cumulative expense line — portfolio total, or the focused tract's total on scoped views. */
  expenseValues: number[];
  payoutMarkers: ChartMarker[];
  breakeven: ChartMarker | null;
  legend: ChartLegendItem[];
}

/** Return-curve (our NRI) color — shared by the chart series + legend. */
export const RETURN_COLOR = '#0f62fe';
/** Expense-line (our WI) color — shared by the chart series + legend. */
export const EXPENSE_COLOR = '#da1e28';
/** Breakeven-marker color. */
export const BREAKEVEN_COLOR = '#24a148';
/** Return / expense series labels (chart series names + legend). */
export const RETURN_LABEL = 'Cumulative return (our NRI)';
export const EXPENSE_LABEL = 'Cumulative expense (our WI)';

/**
 * Resolves the chart series/markers/legend for a (view, focus) — the pure port of the
 * mock CarryChart's `draw`. Aggregate charts the whole portfolio; the scoped views
 * stack the focused tract's groups (byAsset) or wells (byProperty), and their expense
 * line + breakeven marker recompute against that focused scope.
 *
 * @param {CarryChartModel} model - The chart model.
 * @param {ChartView} view - The active chart mode.
 * @param {string | null} focusTractId - The tract the scoped views focus on.
 * @return {ChartRenderData} The draw data.
 */
export function resolveChartData(
  model: CarryChartModel,
  view: ChartView,
  focusTractId: string | null
): ChartRenderData {
  const months = model.months;
  const scoped = view !== 'aggregate' && focusTractId !== null;
  const scopedGroups = model.groups.filter(
    (group) => !scoped || group.tractId === focusTractId
  );

  if (view === 'aggregate') {
    return {
      months,
      aggregateReturn: model.aggReturn,
      stackRows: null,
      expenseValues: model.aggExpense,
      payoutMarkers: payoutMarkersFor(model, scoped, focusTractId),
      breakeven: portfolioBreakeven(model),
      legend: [
        { label: RETURN_LABEL, color: RETURN_COLOR },
        { label: EXPENSE_LABEL, color: EXPENSE_COLOR },
      ],
    };
  }

  const rows =
    view === 'byAsset'
      ? model.groups.map((group, i) => ({
          id: group.id,
          label: group.label,
          series: group.cumReturn,
          tractId: group.tractId,
          colorIndex: i,
        }))
      : model.wells.map((well, i) => ({
          id: well.id,
          label: well.label,
          series: well.cumReturn,
          tractId: well.tractId,
          colorIndex: i,
        }));
  const drawnRows = rows.filter(
    (row) => !scoped || row.tractId === focusTractId
  );

  // Scoped expense line sums the focused tract's groups so it matches the stack below.
  const expenseValues = scoped
    ? months.map((_, i) =>
        scopedGroups.reduce((sum, group) => sum + (group.cumExpense[i] ?? 0), 0)
      )
    : model.aggExpense;

  return {
    months,
    aggregateReturn: null,
    stackRows: drawnRows.map((row) => ({
      id: row.id,
      label: `${row.label} return`,
      values: row.series,
      color: colorForIndex(row.colorIndex),
    })),
    expenseValues,
    payoutMarkers: payoutMarkersFor(model, scoped, focusTractId),
    breakeven: scoped
      ? scopedBreakeven(scopedGroups, months, focusTractId)
      : portfolioBreakeven(model),
    legend: [
      ...drawnRows.map((row) => ({
        label: `${row.label} return`,
        color: colorForIndex(row.colorIndex),
      })),
      { label: EXPENSE_LABEL, color: EXPENSE_COLOR },
    ],
  };
}

function payoutMarkersFor(
  model: CarryChartModel,
  scoped: boolean,
  focusTractId: string | null
): ChartMarker[] {
  const markers: ChartMarker[] = [];
  model.groups.forEach((group, i) => {
    if (scoped && group.tractId !== focusTractId) {
      return;
    }
    // A group can flip more than once — ONE marker per payout month, naming the well(s)
    // paying out then (same-month payouts merge instead of stacking duplicate labels).
    for (const mark of group.payoutMarks) {
      markers.push({
        month: mark.month,
        label: payoutMarkerLabel(group.label, mark.wellNames),
        color: colorForIndex(i),
      });
    }
  });
  return markers;
}

/**
 * The label for one payout marker: the group label plus the well(s) paying out that month —
 * one or two names in full, three or more as "first +N more". No names (a payload predating
 * per-well payouts) keeps the plain group label.
 *
 * @param {string} groupLabel - The "Tract · Group" chart label.
 * @param {string[]} wellNames - The wells paying out this month.
 * @return {string} The marker label.
 */
function payoutMarkerLabel(groupLabel: string, wellNames: string[]): string {
  if (wellNames.length === 0) {
    return `${groupLabel} payout`;
  }
  const wells =
    wellNames.length <= 2
      ? wellNames.join(', ')
      : `${wellNames[0]} +${wellNames.length - 1} more`;
  return `${groupLabel} — ${wells} payout`;
}

function portfolioBreakeven(model: CarryChartModel): ChartMarker | null {
  if (model.breakevenMonth === null) {
    return null;
  }
  return {
    month: model.breakevenMonth,
    label: 'Portfolio breakeven',
    color: BREAKEVEN_COLOR,
  };
}

function scopedBreakeven(
  scopedGroups: ChartGroupRow[],
  months: number[],
  focusTractId: string | null
): ChartMarker | null {
  // The group label leads with the tract's chart name (`chartTractName`), already short — and
  // shortening it again would drop a Tract held in part's "(N of M Subtracts)".
  const label = focusTractId
    ? `${scopedGroups[0]?.label.split(' · ')[0] ?? 'Asset'} breakeven`
    : 'Breakeven';
  for (let i = 0; i < months.length; i++) {
    const ret = scopedGroups.reduce((sum, g) => sum + (g.cumReturn[i] ?? 0), 0);
    const exp = scopedGroups.reduce(
      (sum, g) => sum + (g.cumExpense[i] ?? 0),
      0
    );
    // Same rule as portfolioKpis: no breakeven before expenses start.
    if (exp > 0 && ret - exp >= 0) {
      return { month: i + 1, label, color: BREAKEVEN_COLOR };
    }
  }
  return null;
}

// ── Completeness gate + terms key (dedup / supersede) ────────────────────

/**
 * Whether a carry item is complete enough for the backend to reprice: ≥1 group with
 * ≥1 well, and every populated group's WI/NRI numeric in [0,100] with a valid trigger
 * + threshold. Never fire on partial/invalid input (keeps the last-good preview).
 *
 * @param {InterestItem} item - The interest item.
 * @return {boolean} True when the item can be repriced.
 */
export function isCarryItemComplete(item: InterestItem): boolean {
  if (item.kind !== 'carry') {
    return false;
  }
  const populated = (item.groups ?? []).filter(
    (group) => group.wellIds.length > 0
  );
  return populated.length > 0 && populated.every(isCarryGroupValid);
}

function isCarryGroupValid(group: CarryGroup): boolean {
  const pct = (value: number | null) =>
    value !== null && Number.isFinite(value) && value >= 0 && value <= 100;
  if (![group.bpoWi, group.bpoNri, group.apoWi, group.apoNri].every(pct)) {
    return false;
  }
  const meta = CARRY_TRIGGER_BY_ID[group.trigger];
  if (!meta) {
    return false;
  }
  if (meta.hasValue) {
    if (!Number.isFinite(group.threshold)) {
      return false;
    }
    if (meta.min !== undefined && group.threshold < meta.min) {
      return false;
    }
    if (meta.max !== undefined && group.threshold > meta.max) {
      return false;
    }
  }
  return true;
}

/**
 * A stable key over a carry item's repriceable terms — drives request de-duplication
 * (skip an unchanged reprice) and supersede-in-flight. Wells are sorted so a reorder
 * that preserves membership doesn't churn a request. The item's top-level roster
 * (`wellIds`) is part of the key: the reprice result depends on the unassigned set
 * (roster minus grouped wells, valued as a full working interest), so two payloads
 * differing only in roster must not de-dupe.
 *
 * @param {InterestItem} item - The interest item.
 * @return {string} The terms key.
 */
export function carryTermsKey(item: InterestItem): string {
  const groups = (item.groups ?? [])
    .map((group) =>
      [
        group.id,
        [...group.wellIds].sort().join(','),
        group.bpoWi,
        group.bpoNri,
        group.apoWi,
        group.apoNri,
        group.trigger,
        group.threshold,
        // The basis reprices the position: it decides what fills the payout account
        // and so when the well converts. Leaving it out left a basis switch showing
        // the OLD basis's figures (found in sandbox UAT).
        group.payoutBasis,
      ].join(':')
    )
    .join('|');
  return `${groups}|roster:${rosterKey(item)}`;
}

// The item's top-level roster as a stable set signature (sorted, order-insensitive).
function rosterKey(item: InterestItem): string {
  return [...(item.wellIds ?? [])].sort().join(',');
}

/**
 * A membership-only signature of a carry item — which well sits in which group, plus the
 * tract's full roster; ignoring group order, empty groups, names, and term values. Drives
 * the immediate reprice on a STRUCTURE change (a group removed, or a well unchecked to
 * unassigned / moved between groups): those are button / multi-select edits that emit no
 * field blur, so the on-blur reprice misses them. A term-value edit leaves this key
 * unchanged, so those keep firing on blur (no per-keystroke churn). Wells landing in no
 * group simply drop out of the pair list, so pushing a well to unassigned changes the
 * key. The roster rides along because it defines which wells are unassigned, and
 * unassigned wells are valued in the preview — so the roster arriving or changing is
 * itself a structure change that must reprice.
 *
 * @param {InterestItem} item - The interest item.
 * @return {string} The membership signature.
 */
export function carryMembershipKey(item: InterestItem): string {
  const pairs: string[] = [];
  for (const group of item.groups ?? []) {
    for (const wellId of group.wellIds) {
      pairs.push(`${wellId}:${group.id}`);
    }
  }
  return `${pairs.sort().join('|')}|roster:${rosterKey(item)}`;
}

/**
 * The payout-metric signature of a carry item — which trigger each POPULATED group uses;
 * ignoring thresholds, WI/NRI, names, group order, and empty groups. The payout-metric
 * Dropdown is the one term control whose selection emits no field blur (its menu keeps
 * focus on the toggle), so the on-blur reprice misses a trigger change; the preview hook
 * pairs this with `carryMembershipKey` to reprice immediately. Thresholds are left out on
 * purpose: slider drags and keystrokes stay blur-driven, so they never burst-reprice.
 *
 * @param {InterestItem} item - The interest item.
 * @return {string} The trigger signature.
 */
export function carryTriggerKey(item: InterestItem): string {
  return (item.groups ?? [])
    .filter((group) => group.wellIds.length > 0)
    .map((group) => `${group.id}:${group.trigger}`)
    .sort()
    .join('|');
}
